import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  Alert
} from '@mui/material';
import InteractiveBookingCalendar from '../InteractiveBookingCalendar';
import MobileBookingCalendar from '../MobileBookingCalendar';
import { useTranslation } from '../../hooks/useTranslation';

const BookingDialog = ({
  open,
  onClose,
  salon,
  bookingForm,
  setBookingForm,
  selectedService,
  onServiceChange,
  onTimeSlotSelect,
  onSubmit,
  isMobile = false
}) => {
  const [localError, setLocalError] = useState('');
  const { t } = useTranslation();

  const handleServiceChange = (serviceName) => {
    setLocalError(''); // Clear error when user makes changes
    onServiceChange(serviceName);
  };

  const handleSubmit = () => {
    // Validate locally first
    if (!bookingForm.clientName || !bookingForm.clientPhone || !bookingForm.service || !bookingForm.selectedDateTime) {
      setLocalError(t('booking.fillAllFieldsAndSelectTime'));
      return;
    }
    
    setLocalError('');
    onSubmit();
  };

  const handleClose = () => {
    setLocalError('');
    onClose();
  };

  const handleFormChange = (field, value) => {
    setLocalError(''); // Clear error when user makes changes
    setBookingForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          m: isMobile ? 0 : 2
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('booking.bookAppointment')}
        </Typography>
        <Typography variant="body2" sx={{ color: '#6B7280' }}>
          {salon?.salonName}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ px: 3 }}>
        {localError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {localError}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            fullWidth
            label={t('booking.yourName')}
            value={bookingForm.clientName}
            onChange={(e) => handleFormChange('clientName', e.target.value)}
            required
            size={isMobile ? "medium" : "small"}
          />
          
          <TextField
            fullWidth
            label={t('booking.phone')}
            value={bookingForm.clientPhone}
            onChange={(e) => handleFormChange('clientPhone', e.target.value)}
            required
            size={isMobile ? "medium" : "small"}
          />
          
          <TextField
            fullWidth
            label={t('booking.email')}
            type="email"
            value={bookingForm.clientEmail}
            onChange={(e) => handleFormChange('clientEmail', e.target.value)}
            size={isMobile ? "medium" : "small"}
          />
          
          <FormControl fullWidth required size={isMobile ? "medium" : "small"}>
            <InputLabel>{t('booking.desiredService')}</InputLabel>
            <Select
              value={bookingForm.service}
              label={t('booking.desiredService')}
              onChange={(e) => handleServiceChange(e.target.value)}
            >
              {salon?.services?.map((service) => (
                <MenuItem key={service.id} value={service.name}>
                  {service.name} - {service.price}€ ({service.duration}min)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {selectedService && (
            isMobile ? (
              <MobileBookingCalendar
                salon={salon}
                selectedService={selectedService}
                onTimeSlotSelect={onTimeSlotSelect}
                selectedDateTime={bookingForm.selectedDateTime}
              />
            ) : (
              <InteractiveBookingCalendar
                salon={salon}
                selectedService={selectedService}
                onTimeSlotSelect={onTimeSlotSelect}
                selectedDateTime={bookingForm.selectedDateTime}
              />
            )
          )}
          
          <TextField
            fullWidth
            label={t('booking.notesOptional')}
            multiline
            rows={isMobile ? 3 : 2}
            value={bookingForm.notes}
            onChange={(e) => handleFormChange('notes', e.target.value)}
            size={isMobile ? "medium" : "small"}
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={handleClose} sx={{ color: '#6B7280' }}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            backgroundColor: '#000000',
            color: '#FFFFFF',
            '&:hover': { backgroundColor: '#1F1F1F' }
          }}
        >
          {t('booking.confirmBooking')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookingDialog;